import { useSipStore } from '@bv/stores';
import { sip as sipClass } from '@bv/utils';
import { defineStore } from 'pinia';

import conversationsApi from '@/api/conversations/conversations.api';
import { CallType, EndedCallData } from '@/types/call-center/call-center.types';

export const useCallCenterStore = defineStore('callCenter', {
  state: () => ({
    currentCallsData: {} as Record<CallType['callId'], CallType>,
    isChannelConnected: {} as Record<CallType['callId'], boolean>,
    isNumberSaved: {} as Record<CallType['callId'], boolean>,
    anyVoipChannelConnected: false as boolean,
    lastEndedCall: null as EndedCallData | null,
    callChecks: {} as Record<CallType['callId'], Promise<void>>,
  }),
  actions: {
    setCurrentCall(call: CallType): void {
      this.currentCallsData[call.callId] = call;

      this.callChecks[call.callId] = Promise.all([
        this.checkChannelConnected(call.callId),
        this.checkNumberSaved(call.callId),
      ]).then(() => undefined);
    },
    async deleteCurrentCall(call: CallType): Promise<void> {
      await this.callChecks[call.callId];

      this.lastEndedCall = {
        call,
        isChannelConnected: this.isChannelConnected[call.callId],
        isNumberSaved: this.isNumberSaved[call.callId],
      };

      delete this.currentCallsData[call.callId];
      delete this.isChannelConnected[call.callId];
      delete this.isNumberSaved[call.callId];
      delete this.callChecks[call.callId];
    },
    resetLastCall(): void {
      this.lastEndedCall = null;
    },
    async checkChannelConnected(id: string): Promise<void> {
      try {
        const response = await conversationsApi.get({ callId: id }, 'calls/is-channel-connected');
        this.isChannelConnected[id] = !!response.data.data;
      } catch {
        delete this.isChannelConnected[id];
      }
    },
    async checkNumberSaved(id: string): Promise<void> {
      try {
        const response = await conversationsApi.get({ callId: id }, 'calls/is-number-saved');
        this.isNumberSaved[id] = !!response.data.data;
      } catch {
        delete this.isNumberSaved[id];
      }
    },
    async isAnyVoipChannelConnected(): Promise<void> {
      const sipStore = useSipStore();

      try {
        const response = await conversationsApi.get({}, 'calls/is-any-voip-channel-connected');
        this.anyVoipChannelConnected = response.data.data;
        if (this.anyVoipChannelConnected) {
          await sipClass.disconnect();
          sipStore.setOptions({
            connection: 'register',
          });
          await sipClass.connect();
        } else {
          this.disconnectSip();
        }
      } catch {
        this.anyVoipChannelConnected = false;
        this.disconnectSip();
      }
    },
    disconnectSip(): void {
      const sipStore = useSipStore();

      sipClass.disconnect();
      sipStore.setOptions({
        connection: false,
      });
    },
  },
});
